import { BrandId } from 'utils/override';
import { getBlockSkipKey } from '../../helpers/blockSkipKey';

// Minimal shape needed to decide the skip — accepts both the mapped
// CmsBaseComponent union and PageContent's CmsComponentProps.
type SkippableComponent =
  | { __typename?: string; id?: string | null; visible?: boolean | null }
  | null
  | undefined;

/**
 * Skip keys (see `getBlockSkipKey`) of standalone M3a Trust Strip blocks to
 * skip in BlockMapper because the M3 Plan Tier Selector renders the Trust
 * Strip inside its own left column (CMS-1864). Only skip when the selector
 * will actually render it: the same brand gate as `PlanTierSelectorWrapper`
 * (singsaver / moneyhero) and a visible selector (BlockMapper skips
 * `visible:false`). Passing these to `skipKeys` rather than filtering the
 * component out keeps the position-based `content-${index}` anchors stable.
 *
 * Shared by PageContent and ArticleContent — a TI provider page can render
 * through either container, so both must skip the standalone Trust Strip.
 */
export const getTrustStripSkipKeys = (
  components: readonly SkippableComponent[],
  brand: BrandId,
): string[] => {
  if (brand !== 'singsaver' && brand !== 'moneyhero') {
    return [];
  }

  const hasVisiblePlanTierSelector = components.some(
    (component) =>
      component?.__typename ===
        'ComponentPagesComponentPlanTierSelectorComponent' &&
      component.visible !== false,
  );
  if (!hasVisiblePlanTierSelector) {
    return [];
  }

  return components
    .filter((component) => component?.__typename === 'TrustStripComponent')
    .filter((component) => !!component?.id)
    .map((component) => getBlockSkipKey(component));
};
